import type { SanityDocument } from '@sanity/client';
import { useSanity } from '../../../../hooks/useSanity';
import useLocalization from '../../../../hooks/useLocalization';
import { useSports } from '../../../../queries';
import { useCasinoCategories } from '../../../../queries/casino';
import { determineLinkForwarding } from '../../../../utils/footer';

const MAX_DYNAMIC_LINKS = 10;

export type NeobetFooterLink = {
  id: string;
  title: string;
  to: string;
};

export type NeobetFooterSection = {
  id: string;
  heading: string;
  links: NeobetFooterLink[];
};

const mapCmsLinks = (content: SanityDocument[] | undefined): NeobetFooterLink[] =>
  content?.map((item) => ({ id: item._id, title: item.title, to: determineLinkForwarding(item) })) ?? [];

export const useNeobetFooterData = () => {
  const { t } = useLocalization();
  const { data: policyContent, isLoading: policiesLoading } = useSanity<SanityDocument>('FooterPolicyContent');
  const { data: promoContent, isLoading: promosLoading } = useSanity<SanityDocument>('FooterPromoContent');
  const { data: supportContent, isLoading: supportLoading } = useSanity<SanityDocument>('FooterSupportContent');
  const { data: aboutContent, isLoading: aboutLoading } = useSanity<SanityDocument>('FooterAboutUsContent');
  const { data: casinoCategories = [], isLoading: casinoCategoriesLoading } = useCasinoCategories();
  const { data: sports = [], isLoading: sportsLoading } = useSports({
    queryParams: {
      isLive: false,
      includeEventCount: true,
      limit: MAX_DYNAMIC_LINKS,
    },
  });

  const casinoLinks = casinoCategories.slice(0, MAX_DYNAMIC_LINKS).map((category) => ({
    id: category._id,
    title: category.name ?? category.providerCategoryName ?? category._id,
    to: `/casino?category=${category._id}`,
  }));
  const sportsbookLinks = sports.slice(0, MAX_DYNAMIC_LINKS).map((sport) => ({
    id: sport.id,
    title: sport.displayName ?? sport.name,
    to: `/sports/${sport.id}`,
  }));

  const sections: NeobetFooterSection[] = [
    {
      id: 'casino',
      heading: t('footer.casino', 'Casino'),
      links: casinoLinks,
    },
    {
      id: 'sportsbook',
      heading: t('footer.sportsbook', 'Sportsbook'),
      links: sportsbookLinks,
    },
    {
      id: 'policies',
      heading: t('footer.policies', 'Policies'),
      links: mapCmsLinks(policyContent),
    },
    {
      id: 'promos',
      heading: t('footer.promos', 'Promos'),
      links: mapCmsLinks(promoContent),
    },
    {
      id: 'support',
      heading: t('footer.support', 'Support'),
      links: mapCmsLinks(supportContent),
    },
  ];

  return {
    sections,
    aboutBody: aboutContent?.[0]?.body,
    isLoading:
      policiesLoading || promosLoading || supportLoading || aboutLoading || casinoCategoriesLoading || sportsLoading,
  };
};
